/**
 * Relay calendar event form domain layer for CalendarEventDialog.
 *
 * Owns the snapshot/click-target → Formik initial-values translation (new
 * events seeded from a calendar day/month-view selection, existing
 * events parsed from the API's on/off timing + recurrence descriptor shape)
 * and the Yup validation schema, including the non-V2-unit sunrise/sunset
 * offset sign constraints. Pure functions only — no React, no IO.
 *
 * Contains:
 * - snapToFifteenMinutes — rounds a Date to the nearest 15-minute mark
 * - parseRecurrenceDescriptor — backend recurrence descriptor -> form fields
 * - buildRecurrenceDescriptor — form recurrence fields -> backend descriptor
 * - parseCalendarEventInitialValues — event (or null) -> Formik initial values
 * - buildCalendarEventValidationSchema — Yup schema factory
 */
import { parseISO } from 'date-fns';
import * as Yup from 'yup';

const DAY_MAP = { 0: 'SU', 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR', 6: 'SA' };
const REVERSE_DAY_MAP = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };

/**
 * Maps a backend recurrence descriptor to the form's recurrence fields.
 *
 * @param {?object} descriptor - parsed recurrence descriptor (`recurrence.parsed`):
 *   {freq, interval, byday, bymonthday, until, count}. `null`/no `freq` means
 *   the event does not repeat.
 * @returns {object} form recurrence fields: repeat_type, interval, weekdays,
 *   and (monthly only) monthlyType + monthDay or weekday/weekdayOrdinal
 */
export function parseRecurrenceDescriptor(descriptor) {
  if (!descriptor || !descriptor.freq) {
    return { repeat_type: 'never', interval: 1, weekdays: [] };
  }

  const interval = descriptor.interval ? parseInt(descriptor.interval, 10) : 1;

  switch (String(descriptor.freq).toUpperCase()) {
    case 'DAILY':
      return { repeat_type: 'daily', interval, weekdays: [] };

    case 'WEEKLY':
      return { repeat_type: 'weekly', interval, weekdays: Array.isArray(descriptor.byday) ? descriptor.byday : [] };

    case 'MONTHLY': {
      if (descriptor.bymonthday != null) {
        return {
          repeat_type: 'monthly',
          interval,
          weekdays: [],
          monthlyType: 'date',
          monthDay: parseInt(descriptor.bymonthday, 10),
        };
      }

      const [ordinalEntry] = Array.isArray(descriptor.byday) ? descriptor.byday : [];
      const match = ordinalEntry ? String(ordinalEntry).match(/^(-?\d+)([A-Z]{2})$/) : null;
      if (match) {
        const [, ordinal, dayCode] = match;
        return {
          repeat_type: 'monthly',
          interval,
          weekdays: [],
          monthlyType: 'weekday',
          weekdayOrdinal: parseInt(ordinal, 10),
          weekday: REVERSE_DAY_MAP[dayCode],
        };
      }

      return { repeat_type: 'monthly', interval, weekdays: [] };
    }

    default:
      return { repeat_type: 'never', interval: 1, weekdays: [] };
  }
}

/**
 * Maps the form's recurrence fields to the backend recurrence descriptor —
 * the inverse of `parseRecurrenceDescriptor`.
 *
 * @param {object} recurrenceValues - Formik `recurrence.*` values: repeat_type,
 *   interval, weekdays, monthDay, weekday, weekdayOrdinal, monthlyType
 * @returns {?object} the descriptor {freq, interval, byday?, bymonthday?}, or
 *   `null` when repeat_type is 'never'/absent
 */
export function buildRecurrenceDescriptor(recurrenceValues) {
  const { repeat_type, interval = 1, weekdays, monthDay, weekday, weekdayOrdinal } = recurrenceValues || {};

  if (!repeat_type || repeat_type === 'never') {
    return null;
  }

  switch (repeat_type) {
    case 'daily':
      return { freq: 'DAILY', interval };

    case 'weekly':
      return { freq: 'WEEKLY', interval, byday: weekdays || [] };

    case 'monthly':
      if (monthDay) {
        return { freq: 'MONTHLY', interval, bymonthday: monthDay };
      }
      if (weekday != null && weekdayOrdinal) {
        return { freq: 'MONTHLY', interval, byday: [`${weekdayOrdinal}${DAY_MAP[weekday]}`] };
      }
      return { freq: 'MONTHLY', interval };

    default:
      return null;
  }
}

/** Rounds a Date to the nearest 15-minute mark, zeroing seconds/ms. */
export function snapToFifteenMinutes(date) {
  const newDate = new Date(date);
  const minutes = newDate.getMinutes();
  const snappedMinutes = Math.round(minutes / 15) * 15;
  newDate.setMinutes(snappedMinutes, 0, 0); // Set seconds and milliseconds to 0
  return newDate;
}

/**
 * Builds Formik initial values for the calendar event form.
 *
 * @param {object|null} event - the existing event (API shape), or null/undefined to seed a new event
 * @param {object}      seed
 * @param {string|number} seed.relayId - relay id to attach to a brand-new event
 * @param {Date|string|null} [seed.selectedDate] - day-view Date (with time) or
 *   month-view date string; ignored when `event` is present
 * @param {string} [seed.siteTimeZone] - site IANA timezone (debug logging only)
 * @returns {object} Formik initial values
 */
export function parseCalendarEventInitialValues(event, { relayId, selectedDate, siteTimeZone }) {
  if (!event) {
    // For new events, use selectedDate if provided (from day view click)
    let startDate, defaultTime;

    if (selectedDate instanceof Date) {
      // The day-view click gives a Date whose UTC fields carry the clicked
      // wall-clock time (e.g. a 2:00 AM EST click arrives as a Date with UTC
      // time showing 2:00) rather than a true UTC instant, so the UTC fields
      // are read as if they were local, not converted.

      // Create a new date using the UTC values as if they were local
      const utcYear = selectedDate.getUTCFullYear();
      const utcMonth = selectedDate.getUTCMonth();
      const utcDate = selectedDate.getUTCDate();
      const utcHours = selectedDate.getUTCHours();
      const utcMinutes = selectedDate.getUTCMinutes();

      defaultTime = new Date(utcYear, utcMonth, utcDate, utcHours, utcMinutes, 0, 0);
      defaultTime = snapToFifteenMinutes(defaultTime);

      startDate = new Date(utcYear, utcMonth, utcDate, 0, 0, 0, 0);
    } else if (selectedDate) {
      // String date (from month view) - just the date, no time
      const baseDate = new Date(selectedDate);
      startDate = new Date(baseDate);
      startDate.setHours(0, 0, 0, 0);
      defaultTime = new Date(baseDate);
      defaultTime.setHours(12, 0, 0, 0); // Default to noon
      defaultTime = snapToFifteenMinutes(defaultTime);
    } else {
      // No date provided - use current date/time
      const now = new Date();
      startDate = new Date(now);
      startDate.setHours(0, 0, 0, 0);
      defaultTime = snapToFifteenMinutes(new Date(now));
    }

    const endTime = new Date(defaultTime.getTime() + 60 * 60 * 1000); // 1 hour later

    return {
      name: '',
      // '' (not null) when no relay is preselected — a null Select value
      // permanently latches MUI's uncontrolled mode and breaks label shrink.
      relay_id: relayId ?? '',
      enabled: true, // Always enabled - UI option removed
      always_on: false,
      start_date: startDate,
      end_date: startDate,
      on_timing: {
        timing_type: 'time',
        time: defaultTime,
        offset: 0,
      },
      off_timing: {
        timing_type: 'time',
        time: endTime,
        offset: 0,
      },
      recurrence: {
        exception_dates: [],
        ...parseRecurrenceDescriptor(null),
      },
    };
  }

  // Parse existing event - handle new API format
  return {
    id: event.id,
    name: event.name || event.title || '',
    relay_id: event.relay?.id || event.relay_id,
    enabled: true, // Always enabled - UI option removed
    always_on: event.always_on ?? false,
    start_date: event.start_date ? parseISO(event.start_date) : new Date(),
    end_date: event.end_date ? parseISO(event.end_date) : new Date(),
    on_timing: {
      timing_type: event.on_timing?.type || event.on_timing?.timing_type || 'time',
      time: event.on_timing?.time
        ? (() => {
            const timeDate = parseISO(`2000-01-01T${event.on_timing.time}`);
            timeDate.setSeconds(0, 0); // Set seconds and milliseconds to 0
            return snapToFifteenMinutes(timeDate);
          })()
        : snapToFifteenMinutes(new Date()),
      offset: event.on_timing?.offset || 0,
    },
    off_timing: {
      timing_type: event.off_timing?.type || event.off_timing?.timing_type || 'time',
      time: event.off_timing?.time
        ? (() => {
            const timeDate = parseISO(`2000-01-01T${event.off_timing.time}`);
            timeDate.setSeconds(0, 0); // Set seconds and milliseconds to 0
            return snapToFifteenMinutes(timeDate);
          })()
        : snapToFifteenMinutes(new Date()),
      offset: event.off_timing?.offset || 0,
    },
    recurrence: {
      exception_dates: event.recurrence?.exception_dates || event.exception_dates || [],
      ...parseRecurrenceDescriptor(event.recurrence?.parsed),
    },
  };
}

/**
 * Builds the Yup validation schema for the calendar event form.
 *
 * @param {Function} t - i18next translator (validation message strings)
 * @param {boolean} supportsNegativeOffsets - V2 units allow ON-after/OFF-before
 *   sunrise-sunset offsets; non-V2 units are sign-constrained (ON <= 0, OFF >= 0)
 * @returns {Yup.ObjectSchema} the calendar event validation schema
 */
export function buildCalendarEventValidationSchema(t, supportsNegativeOffsets) {
  return Yup.object({
    name: Yup.string().required(t('calendar.eventDialog.validation.titleRequired')),
    start_date: Yup.date().required(t('calendar.eventDialog.validation.startDateRequired')),
    end_date: Yup.date()
      .required(t('calendar.eventDialog.validation.endDateRequired'))
      .min(Yup.ref('start_date'), t('calendar.eventDialog.validation.invalidDateRange')),
    on_timing: Yup.object().shape({
      timing_type: Yup.string().required(),
      time: Yup.date().when('timing_type', {
        is: val => val === 'time',
        then: schema => schema.required(t('calendar.eventDialog.validation.timeRequired')),
        otherwise: schema => schema.nullable(),
      }),
      offset: Yup.number().when('timing_type', {
        is: val => val !== 'time',
        then: schema => {
          let offsetSchema = schema.required(t('calendar.eventDialog.validation.offsetRequired'));
          // For non-V2 units, ON offset must be <= 0 (at or before sunset/sunrise)
          if (!supportsNegativeOffsets) {
            offsetSchema = offsetSchema
              .min(
                -240,
                t('calendar.eventDialog.validation.offsetBetweenNeg240And0', 'Offset must be between -240 and 0'),
              )
              .max(
                0,
                t(
                  'calendar.eventDialog.validation.onOffsetMustBeNonPositive',
                  'For non-V2 units, ON time must be at or before sunset/sunrise (offset ≤ 0)',
                ),
              );
          } else {
            offsetSchema = offsetSchema
              .min(
                -240,
                t('calendar.eventDialog.validation.offsetBetweenNeg240And240', 'Offset must be between -240 and 240'),
              )
              .max(
                240,
                t('calendar.eventDialog.validation.offsetBetweenNeg240And240', 'Offset must be between -240 and 240'),
              );
          }
          return offsetSchema;
        },
        otherwise: schema => schema.nullable(),
      }),
    }),
    off_timing: Yup.object().shape({
      timing_type: Yup.string().required(),
      time: Yup.date().when('timing_type', {
        is: val => val === 'time',
        then: schema => schema.required(t('calendar.eventDialog.validation.timeRequired')),
        otherwise: schema => schema.nullable(),
      }),
      offset: Yup.number().when('timing_type', {
        is: val => val !== 'time',
        then: schema => {
          let offsetSchema = schema.required(t('calendar.eventDialog.validation.offsetRequired'));
          // For non-V2 units, OFF offset must be >= 0 (at or after sunset/sunrise)
          if (!supportsNegativeOffsets) {
            offsetSchema = offsetSchema
              .min(
                0,
                t(
                  'calendar.eventDialog.validation.offOffsetMustBeNonNegative',
                  'For non-V2 units, OFF time must be at or after sunset/sunrise (offset ≥ 0)',
                ),
              )
              .max(240, t('calendar.eventDialog.validation.offsetBetween0And240', 'Offset must be between 0 and 240'));
          } else {
            offsetSchema = offsetSchema
              .min(
                -240,
                t('calendar.eventDialog.validation.offsetBetweenNeg240And240', 'Offset must be between -240 and 240'),
              )
              .max(
                240,
                t('calendar.eventDialog.validation.offsetBetweenNeg240And240', 'Offset must be between -240 and 240'),
              );
          }
          return offsetSchema;
        },
        otherwise: schema => schema.nullable(),
      }),
    }),
  });
}
